import React, { useContext, useEffect, useMemo, useState } from 'react'
import { SStyled, useSTheme } from '../../../styles/theme'
import SPage from '../../../components/opinionless/SPage'

import DocumentsIntroduction from './DocumentsIntroduction'
import DocumentsProofOfAddress from './DocumentsProofOfAddress'
import DocumentsProofOfAddressUpload from './DocumentsProofOfAddressUpload'
import DocumentsProofOfHouseholdIncome from './DocumentsProofOfHouseholdIncome'
import { OrganizationContext } from '../../../v2/context/organization'
import { ApplicationContext } from '../../../v2/context/application'
import router from 'next/router'
import { DOCUMENTS_TYPES } from 'v2/constants'
import { useApplicationBasicInfo } from '../../../v2/hooks/useApplicationBasicInfo'
import { useApplicationAnswers } from '../../../v2/hooks/useApplicationAnswers'
import { useHouseholdMembers } from '../../../v2/hooks/useHouseholdMembers'
import { useBenefitsAnswers } from '../../../v2/hooks/useBenefitsAnswers'

interface Props {
    applicationId?: string
    category?: string
    onPrevious?: any
    onSuccess?: any
    updatePercentage: any
    requestNewDocumentCategory?: string
    fromBackButton: boolean
}

export const LBPage = SStyled(SPage)`
    margin: 10px 80px;
    padding: 0px;

   ${(p) => `
        @media screen and (max-width: ${p.theme.breakpoints[0]}) {
            margin: 24px 24px;
            padding: 0px;
        }
    `}
`

const DocumentsWizard: React.FC<Props> = (props: Props) => {
    const [currentStep, setCurrentStep] = useState(props.fromBackButton ? 4 : 1)
    const organizationContext = useContext(OrganizationContext)
    const applicationContext = useContext(ApplicationContext)
    const [selectedDocument, setSelectedDocument] = useState('')

    useApplicationBasicInfo()
    useApplicationAnswers()
    useHouseholdMembers()
    useBenefitsAnswers()

    const theme = useSTheme()
    const longBeachFontFamily = theme.fonts.longBeach

    useEffect(() => {
        if (props.requestNewDocumentCategory && applicationContext.documents.length && currentStep === 1) {
            const query = new URLSearchParams(window.location.search)
            const newDocumentRequested = query.get('type')
            const memberId = query.get('member')
            let alreadyUploaded = false

            switch (newDocumentRequested) {
                case DOCUMENTS_TYPES.PROOF_OF_RESIDENCY:
                    alreadyUploaded =
                        applicationContext.documents.filter((document) => {
                            return (
                                document.node.documentType === 'PROOF_OF_RESIDENCY' &&
                                document.node.name &&
                                (document.node.householdMember?.id === memberId ||
                                    document.node.householdMember?.id === atob(memberId).split(':')[1] ||
                                    document.node.person.id === atob(memberId).split(':')[1] ||
                                    document.node.person.id === memberId)
                            )
                        }).length > 0
                    !alreadyUploaded ? setCurrentStep(2) : props.onSuccess()
                    break
                case DOCUMENTS_TYPES.PROOF_OF_INCOME:
                    alreadyUploaded =
                        applicationContext.documents.filter((document) => {
                            return (
                                document.node.documentType === 'PROOF_OF_INCOME' &&
                                document.node.name &&
                                (document.node.householdMember?.id === memberId ||
                                    document.node.householdMember?.id === atob(memberId).split(':')[1] ||
                                    document.node.person.id === atob(memberId).split(':')[1] ||
                                    document.node.person.id === memberId)
                            )
                        }).length > 0
                    !alreadyUploaded ? setCurrentStep(4) : props.onSuccess()
                    break
                default:
                    setCurrentStep(1)
                    break
            }
        }
    }, [props.requestNewDocumentCategory, applicationContext.documents])

    useEffect(() => {
        if (applicationContext.application.id) {
            router.replace(
                `${organizationContext.program.urlFragment}/apply/${applicationContext.application.id}/documents`
            )
        }
    }, [])

    return (
        <div>
            {currentStep === 1 && (
                <DocumentsIntroduction
                    onPrevious={props.onPrevious}
                    nextStep={() => setCurrentStep(2)}
                    fontFamily={longBeachFontFamily}
                    updatePercentage={props.updatePercentage}
                />
            )}
            {currentStep === 2 && (
                <DocumentsProofOfAddress
                    onPrevious={() => setCurrentStep(1)}
                    nextStep={() => {
                        setCurrentStep(3)
                    }}
                    setSelectedDocument={setSelectedDocument}
                    fontFamily={longBeachFontFamily}
                    updatePercentage={props.updatePercentage}
                    disableBackButton={!!props.requestNewDocumentCategory}
                />
            )}
            {currentStep === 3 && (
                <DocumentsProofOfAddressUpload
                    onPrevious={() => setCurrentStep(2)}
                    nextStep={() => {
                        !window.location.search ? setCurrentStep(4) : props.onSuccess()
                    }}
                    selectedDocument={selectedDocument}
                    fontFamily={longBeachFontFamily}
                    updatePercentage={props.updatePercentage}
                />
            )}
            {currentStep === 4 && (
                <DocumentsProofOfHouseholdIncome
                    onPrevious={() => setCurrentStep(3)}
                    nextStep={() => (!window.location.search ? setCurrentStep(5) : props.onSuccess())}
                    fontFamily={longBeachFontFamily}
                    onSuccess={props.onSuccess}
                    updatePercentage={props.updatePercentage}
                    requestedNewDocument={props.requestNewDocumentCategory}
                    fromBackButton={props.fromBackButton}
                />
            )}
        </div>
    )
}

export default DocumentsWizard
